import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ApiClientError } from "../lib/api.js";
import { createResearchJob } from "../lib/researchJobs.js";

type FormState = {
  query: string;
  region: string;
  targetCount: string;
  shuttle: boolean;
  airportTransfer: boolean;
};

const INITIAL: FormState = {
  query: "",
  region: "",
  targetCount: "20",
  shuttle: true,
  airportTransfer: false,
};

export function NewResearchJobPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(INITIAL);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: Parameters<typeof createResearchJob>[0]) =>
      createResearchJob(input),
    onSuccess: (result) => {
      void navigate(`/research-jobs/${result.research_job_id}`);
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setFieldError(null);

    const query = form.query.trim();
    const region = form.region.trim();
    const targetCount = Number.parseInt(form.targetCount, 10);

    if (!query) {
      setFieldError("Query ist erforderlich.");
      return;
    }
    if (!region) {
      setFieldError("Region ist erforderlich.");
      return;
    }
    if (!Number.isFinite(targetCount) || targetCount < 1 || targetCount > 100) {
      setFieldError("Zielanzahl muss zwischen 1 und 100 liegen.");
      return;
    }

    const criteria: Record<string, unknown> = {};
    if (form.shuttle) criteria.shuttle = true;
    if (form.airportTransfer) criteria.airport_transfer = true;

    mutation.mutate({
      query,
      region,
      target_count: targetCount,
      criteria,
    });
  }

  const apiError =
    mutation.error instanceof ApiClientError
      ? mutation.error.message
      : mutation.error
        ? "Job konnte nicht erstellt werden."
        : null;

  return (
    <div className="mx-auto max-w-xl">
      <p className="mb-6 text-sm text-gray-600">
        Region und Kriterien angeben — keine Hotellisten oder URLs nötig.
      </p>

      <form
        onSubmit={onSubmit}
        className="space-y-5 rounded-xl border border-gray-200 bg-white p-6"
      >
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">
            Query
          </span>
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-violet-500"
            value={form.query}
            onChange={(e) => setForm((f) => ({ ...f, query: e.target.value }))}
            placeholder="Airport-Hotels mit Shuttle"
            required
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">
            Region
          </span>
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-violet-500"
            value={form.region}
            onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
            placeholder="Schweiz"
            required
          />
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">
            Zielanzahl (1–100)
          </span>
          <input
            type="number"
            min={1}
            max={100}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 outline-none focus:border-violet-500"
            value={form.targetCount}
            onChange={(e) =>
              setForm((f) => ({ ...f, targetCount: e.target.value }))
            }
            required
          />
        </label>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-gray-700">Kriterien</legend>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.shuttle}
              onChange={(e) =>
                setForm((f) => ({ ...f, shuttle: e.target.checked }))
              }
            />
            Shuttle
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.airportTransfer}
              onChange={(e) =>
                setForm((f) => ({ ...f, airportTransfer: e.target.checked }))
              }
            />
            Flughafentransfer
          </label>
        </fieldset>

        {fieldError || apiError ? (
          <div className="rounded-lg border border-red-100 bg-red-100/40 px-3 py-2 text-sm text-red-800">
            {fieldError ?? apiError}
            {mutation.error instanceof ApiClientError &&
            mutation.error.code === "KILL_SWITCH" ? (
              <span className="mt-1 block text-xs">
                Research Kill Switch ist aktiv — siehe Settings.
              </span>
            ) : null}
          </div>
        ) : null}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {mutation.isPending ? "Erstelle…" : "Job starten"}
          </button>
          <Link
            to="/research-jobs"
            className="text-sm font-medium text-gray-600 hover:text-gray-800"
          >
            Abbrechen
          </Link>
        </div>
      </form>
    </div>
  );
}
